// src/App.tsx - UPDATED WITH LEGAL PAGES AND WASM ENGINE
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.tsx';
import { AISettingsProvider, useAISettings } from './context/AISettingsContext.tsx';
import { Layout } from './components/Layout.tsx';
import { Home } from './pages/Home.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Documents } from './pages/Documents.tsx';
import { Multiplayer } from './pages/Multiplayer.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { AccountCancelled } from './components/AccountCancelled.tsx';
import { Help } from './pages/Help.tsx';
import { Contact } from './pages/Contact.tsx';
import { PrivacyPolicy } from './pages/PrivacyPolicy.tsx';
import { TermsOfService } from './pages/TermsOfService.tsx';
import PieceCatalog from './components/PieceCatalog.tsx';
import VariantCreator from './components/VariantCreator.tsx';
import PlayOnline from './components/PlayOnline.tsx';
import AISettings from './components/AISettings.tsx';
import Leaderboard from './components/Leaderboard.tsx';
import UserProfile from './components/UserProfile.tsx';
import { initWasmEngine } from './engine/WasmChessEngine.ts';
import { initNativeEngine } from './engine/NativeEngineClient.ts';
import './App.css';

// Loading component
const LoadingScreen: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    flexDirection: 'column',
    gap: '20px'
  }}>
    <div style={{ fontSize: '28px' }}>♟️</div>
    <div style={{ fontSize: '18px', color: '#666' }}>Loading...</div>
  </div>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/" />;
};

// Cancellation Restricted Route - Redirects to profile if user is in cancellation grace period
const CancellationRestrictedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isPendingCancellation } = useAuth();

  // If user is in cancellation grace period, redirect to their profile page
  if (isPendingCancellation && user) {
    return <Navigate to={`/profile/${user.userId}`} replace />;
  }

  return <>{children}</>;
};

// Wrapper components that use the context
const PlayOnlineWithSettings: React.FC = () => {
  const { aiSettings } = useAISettings();
  return <PlayOnline rows={8} cols={8} aiSettings={aiSettings} />;
};

const AISettingsWithContext: React.FC = () => {
  const { aiSettings, updateAISettings } = useAISettings();
  return (
    <AISettings 
      onSettingsChange={updateAISettings}
      initialSettings={aiSettings}
    />
  );
};

// Route Configuration
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes - No Layout */}
      <Route path="/" element={<Home />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/account-cancelled" element={<Layout><AccountCancelled /></Layout>} />

      {/* Public Legal Pages - With Layout */}
      <Route
        path="/privacy"
        element={
          <Layout>
            <PrivacyPolicy />
          </Layout>
        }
      />
      <Route
        path="/terms"
        element={
          <Layout>
            <TermsOfService />
          </Layout>
        }
      />
      <Route
        path="/contact"
        element={
          <Layout>
            <Contact />
          </Layout>
        }
      />

      {/* Protected routes - wrapped with CancellationRestrictedRoute to block access during grace period */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <CancellationRestrictedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </CancellationRestrictedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/play-online"
        element={
          <ProtectedRoute>
            <CancellationRestrictedRoute>
              <Layout>
                <PlayOnlineWithSettings />
              </Layout>
            </CancellationRestrictedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/multiplayer"
        element={
          <ProtectedRoute>
            <CancellationRestrictedRoute>
              <Layout>
                <Multiplayer />
              </Layout>
            </CancellationRestrictedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/piece-catalog"
        element={
          <ProtectedRoute>
            <CancellationRestrictedRoute>
              <Layout>
                <PieceCatalog />
              </Layout>
            </CancellationRestrictedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/variant-creator"
        element={
          <ProtectedRoute>
            <CancellationRestrictedRoute>
              <Layout>
                <VariantCreator />
              </Layout>
            </CancellationRestrictedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai-settings"
        element={
          <ProtectedRoute>
            <CancellationRestrictedRoute>
              <Layout>
                <AISettingsWithContext />
              </Layout>
            </CancellationRestrictedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <CancellationRestrictedRoute>
              <Layout>
                <Leaderboard />
              </Layout>
            </CancellationRestrictedRoute>
          </ProtectedRoute>
        }
      />

      {/* Profile page - NOT wrapped with CancellationRestrictedRoute so users can access reactivation */}
      <Route
        path="/profile/:userId"
        element={
          <ProtectedRoute>
            <Layout>
              <UserProfile />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <CancellationRestrictedRoute>
              <Layout>
                <Documents />
              </Layout>
            </CancellationRestrictedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <CancellationRestrictedRoute>
              <Layout>
                <Help />
              </Layout>
            </CancellationRestrictedRoute>
          </ProtectedRoute>
        }
      />

      {/* Legacy routes */}
      <Route path="/play" element={<Navigate to="/play-online" replace />} />
      <Route path="/settings" element={<Navigate to="/documents" replace />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

// Main App Component
function App() {
  const [wasmReady, setWasmReady] = useState(false);
  const [wasmError, setWasmError] = useState(false);

  useEffect(() => {
    const initEngines = async () => {
      // Initialize WASM engine
      try {
        const success = await initWasmEngine();
        setWasmReady(success);
        if (success) {
          console.log('[App] WASM chess engine initialized successfully');
        } else {
          console.warn('[App] WASM engine failed to load, using TypeScript fallback');
          setWasmError(true);
        }
      } catch (error) {
        console.error('[App] Error initializing WASM engine:', error);
        setWasmError(true);
      }

      // Initialize Native (Rust) engine - runs in parallel conceptually
      try {
        console.log('[App] Initializing Native Rust engine...');
        const nativeAvailable = await initNativeEngine();
        console.log(`[App] Native Rust engine: ${nativeAvailable ? 'Available' : 'Not available'}`);
      } catch (error) {
        console.error('[App] Error initializing Native engine:', error);
      }
    };
    initEngines();
  }, []);

  useEffect(() => {
    if (wasmReady) {
      console.log('[App] WASM Status: Available and ready');
    } else if (wasmError) {
      console.log('[App] WASM Status: Unavailable, using fallback');
    }
  }, [wasmReady, wasmError]);

  return (
    <Router>
      <AuthProvider>
        <AISettingsProvider>
          <AppRoutes />
        </AISettingsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
